// A router of a few lines: the History API and a hook. The relay serves
// index.html for /s/*, /r/*, /i/*, /c/*, /device, /shares, /classes and /login.
import { useEffect, useState } from "react";

export interface Location {
  path: string;
  query: URLSearchParams;
}

function current(): Location {
  return { path: window.location.pathname, query: new URLSearchParams(window.location.search) };
}

export function navigate(to: string, replace = false): void {
  if (replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useLocation(): Location {
  const [loc, setLoc] = useState(current);
  useEffect(() => {
    const on = () => setLoc(current());
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  return loc;
}

/** Where to go once signed in, kept across the sign-in round trip. */
const NextKey = "byshare.next";
const NextTtlMs = 60 * 60 * 1000;

export function rememberNext(path: string): void {
  try {
    localStorage.setItem(NextKey, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // storage unavailable: the person lands on their shares instead
  }
}

export function takeNext(): string | null {
  try {
    const raw = localStorage.getItem(NextKey);
    localStorage.removeItem(NextKey);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path: string; at: number };
    // Only a path on this origin, never a URL someone planted.
    if (Date.now() - at > NextTtlMs || !path.startsWith("/") || path.startsWith("//")) return null;
    return path;
  } catch {
    return null;
  }
}
